import React, { useState, useEffect } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { NFTController } from "../../logic/controller/NFTController";
import { PaymentController } from "../../logic/controller/PaymentController";
import { TierController } from "../../logic/controller/TierController";
import { useWallet } from "../../logic/library/wallet/useWallet";
import { tiers, saleState } from "../../logic/const/contract";
import { UserController } from "../../logic/controller/UserController";
import nftImages from "../../logic/const/images.json";
import "../styles/UserView.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import closeImage from "../../assets/images/close.png";
import stripeLogo from "../../assets/images/stripe_logo.svg";
import metaMaskLogo from "../../assets/images/metaMask_logo.png";
import { Numbers } from "ct-library-numbers";
import tier1 from "../../assets/images/tiers/aether.png";
import tier2 from "../../assets/images/tiers/nova.png";
import tier3 from "../../assets/images/tiers/comet.png";
import tier4 from "../../assets/images/tiers/stardust.png";
import { nftTiers } from "./Home";

// shortenAddress 함수 추가
const shortenAddress = (address) => {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * UserView 컴포넌트는 사용자 관련 UI를 제공합니다.
 */
const UserView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentAllowance, setCurrentAllowance] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [tierPrices, setTierPrices] = useState({});
  const [currentSaleState, setCurrentSaleState] = useState(null);
  const [tierList, setTierList] = useState([]);
  const [signupData, setSignupData] = useState({
    email: "",
    first_name: "",
    last_name: ""
  });
  const [userNFTs, setUserNFTs] = useState([]);
  // const [selectedNFT, setSelectedNFT] = useState(null);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isHowToOpen, setIsHowToOpen] = useState(false);
  const [selectedNFTImages, setSelectedNFTImages] = useState([]);
  const [selectedTier, setSelectedTier] = useState(1); // 기본적으로 첫 번째 티어(Aether)가 선택되도록 변경
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedItem, setSelectedItem] = useState();
  const [isOpenedPurchase, setIsOpenedPurchase] = useState(false);
  const [detailItem, setDetailItem] = useState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("Stripe");
  const [createdOrder, setCreatedOrder] = useState(false);

  useEffect(() => {
    const tab = searchParams.get("tab")?.toUpperCase();

    if (tab) {
      if (tab === "MY-NFTS" || tab === "MY NFTS" || tab === "MYNFTS") {
        setSelectedTab(1);
      } else {
        setSelectedTab(0);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedTab]);

  useEffect(() => {
    if (isOpenedPurchase || detailItem || isHowToOpen) {
      document.body.style.overflow = "hidden";
      const el = document.body.getElementsByClassName("modal");
      setTimeout(() => {
        el[0].style.opacity = 1;
        el[0].style.marginTop = 0;
      }, 0);
    } else {
      document.body.style.overflow = "auto";
      setCreatedOrder(false);
    }
  }, [isOpenedPurchase, detailItem]);

  const { isConnected, address, chainId, callContract, sendTransaction, signTypedData, personalSign, connectWallet, disconnectWallet, isConnecting, isDisconnecting } = useWallet();

  const showToast = ({ title, description, status }) => {
    // 이전 토스트를 모두 제거하고 새로운 토스트만 표시
    const id = Math.random().toString();
    setToasts([{ id, title, description, status }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  const nftController = NFTController.getInstance();
  const paymentController = PaymentController.getInstance();
  const tierController = TierController.getInstance();

  // 천 단위 쉼표 추가 함수
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 판매 상태 조회
  const getSaleState = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 판매 상태 조회 데이터 생성
      const saleStateData = nftController.createGetSaleStateData();

      // useWallet 훅의 callContract 함수를 사용하여 판매 상태 조회
      const result = await callContract(saleStateData);

      // 16진수 문자열을 10진수로 변환
      const stateValue = parseInt(result, 16);

      setCurrentSaleState(stateValue);

      // 판매 상태에 따른 메시지 생성
      let stateMessage = "";
      switch (stateValue) {
        case saleState.INACTIVE:
          stateMessage = "Inactive";
          break;
        case saleState.PRESALE:
          stateMessage = "Presale";
          break;
        case saleState.PUBLICSALE:
          stateMessage = "Public Sale";
          break;
        default:
          stateMessage = "Unknown";
      }

      showToast({
        title: "Sale Status Check Success",
        description: `Current Sale State: ${stateMessage}`,
        status: "success"
      });

      return stateValue;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Sale Status Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // 티어 가격 조회
  const getTierPrice = async (tier) => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 티어 가격 조회 데이터 생성
      const tierPriceData = nftController.createGetTierPriceData(tier);

      // useWallet 훅의 callContract 함수를 사용하여 티어 가격 조회
      const result = await callContract(tierPriceData);

      // 16진수 문자열을 10진수 문자열로 변환
      const price = BigInt(result).toString();

      // 티어 가격 상태 업데이트
      setTierPrices((prev) => ({
        ...prev,
        [tier]: price
      }));

      showToast({
        title: "Tier Price Check Success",
        description: `Tier ${tier} Price: ${price} wei`,
        status: "success"
      });

      return price;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Tier Price Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // USDT allowance 조회
  const getAllowance = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 allowance 조회 데이터 생성
      const allowanceData = nftController.createGetAllowanceData({
        ownerAddress: address
      });

      try {
        // useWallet 훅의 callContract 함수를 사용하여 allowance 조회
        const result = await callContract(allowanceData);

        // 16진수 문자열을 10진수 문자열로 변환
        const allowance = result ? BigInt(result).toString() : "0";

        setCurrentAllowance(allowance);
        showToast({
          title: "Allowance Check Success",
          description: `Current USDT Allowance: ${allowance}`,
          status: "success"
        });
        return allowance;
      } catch (callError) {
        // 컨트랙트 호출 실패 시 0으로 설정
        setCurrentAllowance("0");
        showToast({
          title: "Allowance Check Result",
          description: "Current Allowance is 0 or cannot be retrieved.",
          status: "warning"
        });
        return "0";
      }
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Allowance Check Failure",
        description: err.message,
        status: "error"
      });
      return "0";
    } finally {
      setLoading(false);
    }
  };

  const setAllowance = async (allowanceAmount) => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      if (!allowanceAmount || isNaN(allowanceAmount) || parseFloat(allowanceAmount) <= 0) {
        throw new Error("유효한 allowance 양을 입력해주세요.");
      }

      setLoading(true);

      // NFTController를 통해 allowance 설정 데이터 생성
      const allowanceData = nftController.createSetAllowanceData({
        ownerAddress: address,
        amount: allowanceAmount // USDT 단위로 전달
      });

      // useWallet 훅의 sendTransaction 함수를 사용하여 allowance 설정
      const txHash = await sendTransaction(allowanceData);

      showToast({
        title: "Allowance 설정 성공",
        description: `트랜잭션 해시: ${txHash}`,
        status: "success"
      });

      // allowance 조회 업데이트
      await getAllowance();
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Allowance 설정 실패",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // NFT 민팅
  const mintNFT = async (tier) => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }
      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }
      if (tier === undefined || !tiers.includes(tier)) {
        throw new Error("유효한 NFT 티어가 아닙니다.");
      }
      // 판매 상태 확인
      const stateValue = await getSaleState();
      if (stateValue === saleState.INACTIVE) {
        throw new Error("현재 NFT 판매가 비활성화되어 있습니다.");
      }
      // 선택한 티어의 민팅 가능 여부 확인
      if (!isTierMintable(tier)) {
        throw new Error(`티어 ${tier}는 현재 민팅이 불가능합니다. 사용자 남은 발행 가능량이 0 이하입니다.`);
      }
      setLoading(true);

      // NFTController를 통해 민팅 데이터 생성
      const mintData = nftController.createMintNFTData({
        to: address,
        tier: tier
      });
      // useWallet 훅의 sendTransaction 함수를 사용하여 NFT 민팅
      const txHash = await sendTransaction(mintData);

      showToast({
        title: "NFT Mint Success",
        description: `Transaction Hash: ${txHash}`,
        status: "success"
      });
    } catch (err) {
      setError(err.message);
      showToast({
        title: "NFT Mint Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Aether(tier 0) 최대 발행량 조회
  const getAetherMaxSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Aether 최대 발행량 조회 데이터 생성
      const maxSupplyData = nftController.createGetAetherMaxSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Aether 최대 발행량 조회
      const result = await callContract(maxSupplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const maxSupply = BigInt(result).toString();

      showToast({
        title: "Aether Maximum Supply Check Success",
        description: `Aether Maximum Supply: ${maxSupply}`,
        status: "success"
      });

      return maxSupply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Aether Maximum Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Nova(tier 1) 최대 발행량 조회
  const getNovaMaxSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Nova 최대 발행량 조회 데이터 생성
      const maxSupplyData = nftController.createGetNovaMaxSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Nova 최대 발행량 조회
      const result = await callContract(maxSupplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const maxSupply = BigInt(result).toString();

      showToast({
        title: "Nova Maximum Supply Check Success",
        description: `Nova Maximum Supply: ${maxSupply}`,
        status: "success"
      });

      return maxSupply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Nova Maximum Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Comet(tier 2) 최대 발행량 조회
  const getCometMaxSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Comet 최대 발행량 조회 데이터 생성
      const maxSupplyData = nftController.createGetCometMaxSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Comet 최대 발행량 조회
      const result = await callContract(maxSupplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const maxSupply = BigInt(result).toString();

      showToast({
        title: "Comet Maximum Supply Check Success",
        description: `Comet Maximum Supply: ${maxSupply}`,
        status: "success"
      });

      return maxSupply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Comet Maximum Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Stardust(tier 3) 최대 발행량 조회
  const getStardustMaxSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Stardust 최대 발행량 조회 데이터 생성
      const maxSupplyData = nftController.createGetStardustMaxSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Stardust 최대 발행량 조회
      const result = await callContract(maxSupplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const maxSupply = BigInt(result).toString();

      showToast({
        title: "Stardust Maximum Supply Check Success",
        description: `Stardust Maximum Supply: ${maxSupply}`,
        status: "success"
      });

      return maxSupply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Stardust Maximum Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Aether(tier 0) 현재 발행량 조회
  const getAetherSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Aether 현재 발행량 조회 데이터 생성
      const supplyData = nftController.createGetAetherSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Aether 현재 발행량 조회
      const result = await callContract(supplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const supply = BigInt(result).toString();

      showToast({
        title: "Aether Current Supply Check Success",
        description: `Aether Current Supply: ${supply}`,
        status: "success"
      });

      return supply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Aether Current Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Nova(tier 1) 현재 발행량 조회
  const getNovaSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Nova 현재 발행량 조회 데이터 생성
      const supplyData = nftController.createGetNovaSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Nova 현재 발행량 조회
      const result = await callContract(supplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const supply = BigInt(result).toString();

      showToast({
        title: "Nova Current Supply Check Success",
        description: `Nova Current Supply: ${supply}`,
        status: "success"
      });

      return supply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Nova Current Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Comet(tier 2) 현재 발행량 조회
  const getCometSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Comet 현재 발행량 조회 데이터 생성
      const supplyData = nftController.createGetCometSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Comet 현재 발행량 조회
      const result = await callContract(supplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const supply = BigInt(result).toString();

      showToast({
        title: "Comet Current Supply Check Success",
        description: `Comet Current Supply: ${supply}`,
        status: "success"
      });

      return supply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Comet Current Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Stardust(tier 3) 현재 발행량 조회
  const getStardustSupply = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // NFTController를 통해 Stardust 현재 발행량 조회 데이터 생성
      const supplyData = nftController.createGetStardustSupplyData();

      // useWallet 훅의 callContract 함수를 사용하여 Stardust 현재 발행량 조회
      const result = await callContract(supplyData);

      // 16진수 문자열을 10진수 문자열로 변환
      const supply = BigInt(result).toString();

      showToast({
        title: "Stardust Current Supply Check Success",
        description: `Stardust Current Supply: ${supply}`,
        status: "success"
      });

      return supply;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Stardust Current Supply Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // 모든 티어의 최대 발행량과 현재 발행량 조회
  const getAllSupplies = async () => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      setLoading(true);

      // 각 티어별 최대 발행량 조회
      const aetherMaxSupply = await getAetherMaxSupply();
      const novaMaxSupply = await getNovaMaxSupply();
      const cometMaxSupply = await getCometMaxSupply();
      const stardustMaxSupply = await getStardustMaxSupply();

      // 각 티어별 현재 발행량 조회
      const aetherSupply = await getAetherSupply();
      const novaSupply = await getNovaSupply();
      const cometSupply = await getCometSupply();
      const stardustSupply = await getStardustSupply();

      // 사용자 전용 최대 발행량 계산
      const userMaxSupplies = {
        0: aetherMaxSupply,
        1: BigInt(novaMaxSupply) - BigInt(aetherMaxSupply),
        2: BigInt(cometMaxSupply) - BigInt(aetherMaxSupply) * 2n,
        3: BigInt(stardustMaxSupply) - BigInt(aetherMaxSupply) * 3n
      };

      // 모든 발행량 데이터를 객체로 반환
      return {
        maxSupplies: {
          0: aetherMaxSupply,
          1: novaMaxSupply,
          2: cometMaxSupply,
          3: stardustMaxSupply
        },
        currentSupplies: {
          0: aetherSupply,
          1: novaSupply,
          2: cometSupply,
          3: stardustSupply
        },
        userMaxSupplies: userMaxSupplies
      };
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Tier Supply Check Failure",
        description: err.message,
        status: "error"
      });
      return {
        maxSupplies: {},
        currentSupplies: {},
        userMaxSupplies: {}
      };
    } finally {
      setLoading(false);
    }
  };

  // 판매 상태 자동 조회
  useEffect(() => {
    // 컴포넌트 마운트 시 판매 상태 조회
    getSaleState();
  }, []);

  // 지갑 연결 상태 변경 감지
  useEffect(() => {
    if (isConnected && address) {
      // 지갑 연결 시 자동으로 판매 상태 조회
      getSaleState();
      // 지갑 연결 시 자동으로 사용자 정보 확인
      checkUserInfo();
    } else {
      // 지갑 연결 해제 시 상태 초기화
      setCurrentAllowance(null);
      setUserInfo(null);
      setShowSignupForm(false);
      setTierList([]);
      setUserNFTs([]);
      setCurrentSaleState(null);
    }
  }, [isConnected, address]);

  // 사용자 정보 확인 함수
  const checkUserInfo = async () => {
    try {
      setLoading(true);
      const userController = UserController.getInstance();
      const user = await userController.getUser(address);

      if (user) {
        // 사용자 정보가 있는 경우
        setUserInfo(user);
        setShowSignupForm(false);
        // 사용자 정보가 있을 때 allowance 자동 조회
        await getAllowance();
        // 사용자 정보가 있을 때 NFT 목록 자동 조회
        await getUserNFTs();
      } else {
        // 사용자 정보가 없는 경우
        setUserInfo(null);
        setShowSignupForm(true);
        setUserNFTs([]);
      }
    } catch (error) {
      console.error("사용자 정보 확인 오류:", error);
      showToast({
        title: "사용자 정보 확인 실패",
        description: error.message || "사용자 정보를 확인하는 중 오류가 발생했습니다.",
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // 판매 상태에 따른 메시지 반환
  // const getSaleStateMessage = () => {
  //   if (currentSaleState === null) return "";

  //   switch (currentSaleState) {
  //     case saleState.INACTIVE:
  //       return "Inactive";
  //     case saleState.PRESALE:
  //       return "Presale";
  //     case saleState.PUBLICSALE:
  //       return "Public Sale";
  //     default:
  //       return "Unknown";
  //   }
  // };

  // 판매 상태에 따른 클래스 반환
  const getSaleStateClass = () => {
    if (currentSaleState === null) return "";

    switch (currentSaleState) {
      case saleState.INACTIVE:
        return "inactive";
      case saleState.PRESALE:
        return "presale";
      case saleState.PUBLICSALE:
        return "publicsale";
      default:
        return "";
    }
  };

  // 판매 상태에 따른 민팅 가능 여부 확인
  // const isMintingEnabled = () => {
  //   return currentSaleState !== null && currentSaleState !== saleState.INACTIVE;
  // };

  // 특정 티어의 민팅 가능 여부 확인
  const isTierMintable = (tier) => {
    // 현재 선택된 티어의 발행량 정보 가져오기
    const tierInfo = tierList.find((t) => t.id === tier);
    if (!tierInfo) return false;

    const userMaxSupply = BigInt(tierInfo.userMaxSupply);
    const currentSupply = BigInt(tierInfo.currentSupply);

    // 사용자 전용 최대 발행량이 현재 발행량보다 크면 민팅 가능
    return userMaxSupply > currentSupply;
  };

  // 티어 이름 반환
  const getTierName = (tier) => {
    switch (tier) {
      case 1:
        return "Aether";
      case 2:
        return "Nova";
      case 3:
        return "Comet";
      case 4:
        return "Stardust";
      default:
        return `Tier ${tier}`;
    }
  };

  // 티어 목록 데이터 생성
  const getTierList = async () => {
    // 각 tier의 가격을 조회 (지갑 연결 시에만)
    // if (isConnected && address) {
    // for (const tier of tiers) {
    //   if (!tierPrices[tier]) {
    //     try {
    //       await getTierPrice(tier);
    //     } catch (err) {
    //       console.error(`Failed to fetch price for tier ${tier}:`, err);
    //     }
    //   }
    // }

    // 발행량 데이터 조회 (지갑 연결 시에만)
    // let supplies = {
    //   maxSupplies: {},
    //   currentSupplies: {},
    //   userMaxSupplies: {}
    // };

    try {
      const tiers = await tierController.getAll();

      if (isConnected && address) {
        return tiers.map((tier) => {
          return {
            id: tier.tier_id,
            name: tier.name,
            price: tier.price,
            maxSupply: tier.max_supply || 0,
            currentSupply: tier.supply || 0,
            isPurchasable: isConnected && address && currentAllowance && !Numbers.lt(currentAllowance, tier.price)
          };
        });
      }

      return tiers.map((tier) => {
        return {
          id: tier.tier_id,
          name: tier.name,
          price: tier.price,
          maxSupply: 0,
          currentSupply: 0,
          isPurchasable: false
        };
      });
    } catch (err) {
      console.error("Failed to fetch supply data:", err);
    }

    //   return tiers.map((tier) => {
    //     const tierName = getTierName(tier);
    //     const price = tierPrices[tier] || "0";
    //     const maxSupply = supplies.maxSupplies[tier] || "0";
    //     const currentSupply = supplies.currentSupplies[tier] || "0";
    //     const userMaxSupply = supplies.userMaxSupplies[tier] || "0";
    //     const isPurchasable =
    //       isConnected &&
    //       address &&
    //       currentAllowance &&
    //       BigInt(currentAllowance) >= BigInt(price) &&
    //       currentSaleState !== saleState.INACTIVE;

    //     return {
    //       name: tierName,
    //       id: tier,
    //       price: price,
    //       maxSupply: maxSupply,
    //       currentSupply: currentSupply,
    //       userMaxSupply: userMaxSupply,
    //       isPurchasable: isPurchasable,
    //     };
    //   });
    // }

    // // 지갑이 연결되지 않은 경우 기본 티어 정보만 반환
    // return tiers.map((tier) => {
    //   const tierName = getTierName(tier);
    //   return {
    //     name: tierName,
    //     id: tier,
    //     price: "0",
    //     maxSupply: "0",
    //     currentSupply: "0",
    //     userMaxSupply: "0",
    //     isPurchasable: false,
    //   };
    // });
  };

  // 티어 목록 업데이트
  useEffect(() => {
    const updateTierList = async () => {
      // 지갑 연결 여부와 관계없이 티어 목록 업데이트
      const list = await getTierList();
      setTierList(list);
    };
    updateTierList();
  }, [isConnected, address, currentAllowance, currentSaleState, tierPrices]);

  // location state에서 selectedTab을 받아서 selectedTier 설정
  useEffect(() => {
    if (location.state?.selectedTab) {
      const tierMap = {
        aether: 0,
        nova: 1,
        comet: 2,
        stardust: 3
      };
      const tierIndex = tierMap[location.state.selectedTab.toLowerCase()];
      if (tierIndex !== undefined) {
        setSelectedTier(tierIndex);
      }
    }
  }, [location.state]);

  /**
   * 회원가입을 처리합니다.
   */
  const signUp = async () => {
    if (!isConnected || !address) {
      showToast({
        title: "회원가입 실패",
        description: "지갑을 연결해주세요.",
        status: "error"
      });
      return;
    }

    try {
      setLoading(true);
      const userController = UserController.getInstance();

      // 1. 서명 데이터 생성
      const signData = userController.createSignature(address);

      // 2. 서명 - personalSign으로 변경
      const signature = await personalSign(JSON.stringify(signData));

      // 3. 회원가입 요청
      const result = await userController.signup({
        ...signupData,
        address,
        signature
      });

      showToast({
        title: "회원가입 성공",
        description: "회원가입이 완료되었습니다.",
        status: "success"
      });

      // 입력 필드 초기화
      setSignupData({
        email: "",
        first_name: "",
        last_name: ""
      });

      // 회원 정보 갱신
      await checkUserInfo();
    } catch (error) {
      console.error("Signup error:", error);
      showToast({
        title: "회원가입 실패",
        description: error.message || "회원가입 중 오류가 발생했습니다.",
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // 사용자 NFT 목록 조회 함수
  const getUserNFTs = async () => {
    try {
      if (!address) {
        throw new Error("지갑 주소가 필요합니다.");
      }

      setLoading(true);

      const nfts = await nftController.getNFT(address);

      if (nfts && nfts.length > 0) {
        // 각 NFT에 대해 토큰 URI 조회 및 메타데이터 가져오기
        const newNFTs = nfts.map((nft) => {
          return {
            ...nft,
            image_url: nft.image_metadata ? nft.image_metadata.image : ""
          };
        });

        setUserNFTs(newNFTs);
      } else {
        setUserNFTs([]);
      }
    } catch (error) {
      console.error("NFT 목록 조회 오류:", error);
      showToast({
        title: "NFT 목록 조회 실패",
        description: error.message || "NFT 목록을 조회하는 중 오류가 발생했습니다.",
        status: "error"
      });
      setUserNFTs([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get NFT images by tier
  const getNFTImagesByTier = (tier) => {
    const tierData = nftImages.find((t) => t.tier === tier);
    return tierData ? tierData.images : [];
  };

  // NFT 이미지 설정
  const setNFTImage = async (nft, image) => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      if (!nft) {
        throw new Error("NFT 정보가 필요합니다.");
      }

      if (!image.image_id) {
        throw new Error("이미지 ID가 필요합니다.");
      }

      setImageLoading(true);

      const timestamp = Date.now();
      // 서명 데이터 생성
      // const signData = {
      //   address: address,
      //   tokenId: nft.nft_info_id,
      //   tier: nft.tier_id,
      //   imageId: image.image_id,
      //   timestamp
      // };

      // 서명 데이터를 문자열로 변환
      // const signDataString = JSON.stringify(signData);

      // personalSign을 사용하여 서명
      // const signature = await personalSign(signDataString);

      // NFT 이미지 설정 address, nftInfoId, tierId, metadata
      const updatedNFT = await nftController.setNFTImage(address, nft.nft_info_id, nft.tier_id, image);

      // NFT 목록 업데이트
      const updatedNFTs = userNFTs.map((item) =>
        item.nft_info_id === nft.nft_info_id
          ? {
              ...updatedNFT,
              image_url: updatedNFT.image_metadata ? updatedNFT.image_metadata.image : ""
            }
          : item
      );

      setUserNFTs(updatedNFTs);

      showToast({
        title: "NFT Image Set Success",
        description: "NFT Image has been successfully set.",
        status: "success"
      });

      return updatedNFT;
    } catch (err) {
      setError(err.message);
      showToast({
        title: "NFT Image Set Failure",
        description: err.message,
        status: "error"
      });
      throw err;
    } finally {
      setImageLoading(false);
      setDetailItem();
    }
  };

  // NFT 토큰 URI 조회
  const getTokenURI = async (tokenId) => {
    try {
      if (!isConnected) {
        throw new Error("지갑이 연결되어 있지 않습니다.");
      }

      if (!address) {
        throw new Error("지갑 주소를 찾을 수 없습니다.");
      }

      if (!tokenId) {
        throw new Error("토큰 ID가 필요합니다.");
      }

      setLoading(true);

      // 토큰 URI 조회를 위한 트랜잭션 데이터 생성
      const txData = nftController.createGetTokenURIData(tokenId);
      // 트랜잭션 실행
      const result = await callContract(txData);
      const decoded = nftController.decodeTokenURI(result);
      // 결과 처리
      if (decoded) {
        showToast({
          title: "Token URI Check Success",
          description: `Token URI: ${decoded}`,
          status: "success"
        });

        return decoded;
      }
    } catch (err) {
      setError(err.message);
      showToast({
        title: "Token URI Check Failure",
        description: err.message,
        status: "error"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };
  const createOrder = async () => {
    const metaData = {
      image_id: selectedItem.image_id,
      image: selectedItem.image,
      name: selectedItem.name
    };

    setLoading(true);

    try {
      const res = await paymentController.create(userInfo.address, selectedTier, metaData);

      if (res.url) {
        window.open(res.url);
        setCreatedOrder(true);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      showToast({
        title: "Token URI Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setCurrentAllowance(0);
      setLoading(false);
    }
  };

  const createOrderByWeb3 = async () => {
    if (!isConnected) {
      throw new Error("지갑이 연결되어 있지 않습니다.");
    }
    if (!address) {
      throw new Error("지갑 주소를 찾을 수 없습니다.");
    }
    // if (tier === undefined || !tiers.includes(tier)) {
    //   throw new Error("유효한 NFT 티어가 아닙니다.");
    // }
    setLoading(true);
    try {
      const buyNFTData = await nftController.createBuyNFTData(userInfo.address, selectedTier, selectedItem.image_id);

      // useWallet 훅의 sendTransaction 함수를 사용하여 NFT 민팅
      const txHash = await sendTransaction(buyNFTData);
      setCreatedOrder(true);

      // showToast({
      //   title: "NFT Mint Success",
      //   description: `Transaction Hash: ${txHash}`,
      //   status: "success"
      // });
    } catch (err) {
      console.error(err);
      setError(err.message);
      showToast({
        title: "Token URI Check Failure",
        description: err.message,
        status: "error"
      });
    } finally {
      setLoading(false);
      setCurrentAllowance(0);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <Header />

      <div className="max-w-7xl mx-auto px-4" style={{ marginBottom: 100 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: 30
          }}
        >
          <h1 className="text-3xl font-bold mb-2 mt-12">NFT Marketplace Presale</h1>
          <button
            aria-label="How to Purchase"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginLeft: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 32,
              width: 32,
              position: "relative"
            }}
            onClick={() => setIsHowToOpen(true)}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector(".tooltip");
              if (tooltip) {
                tooltip.style.opacity = "1";
                tooltip.style.transform = "translateY(0)";
              }
            }}
            onMouseLeave={(e) => {
              const tooltip = e.currentTarget.querySelector(".tooltip");
              if (tooltip) {
                tooltip.style.opacity = "0";
                tooltip.style.transform = "translateY(10px)";
              }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7B3FE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div
              className="tooltip"
              style={{
                position: "absolute",
                bottom: "100%",
                left: -49,
                transform: "translateX(-50%) translateY(10px)",
                backgroundColor: "#7B3FE4",
                color: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                opacity: 0,
                transition: "all 0.2s ease",
                pointerEvents: "none",
                marginBottom: "8px",
                boxShadow: "0 4px 12px rgba(123, 63, 228, 0.2)"
              }}
            >
              How to Purchase
              <div
                style={{
                  position: "absolute",
                  bottom: "-4px",
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#7B3FE4"
                }}
              />
            </div>
          </button>
        </div>

        {/* How to Purchase 모달 */}
        {isHowToOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onClick={() => setIsHowToOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                maxWidth: 600,
                width: "90%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                padding: 32,
                position: "relative",
                animation: "fadeIn .2s"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsHowToOpen(false)}
                style={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4
                }}
                aria-label="Close How to Purchase"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <h2 className="text-2xl font-bold mb-6" style={{ color: "#7B3FE4" }}>
                How to Purchase
              </h2>
              <p className="text-gray-600 mb-6">Follow these steps to purchase your NFT</p>
              <div style={{ marginBottom: 32, display: "flex", gap: 16 }}>
                <button
                  onClick={() => setSelectedPaymentMethod("Stripe")}
                  style={{
                    boxShadow: selectedPaymentMethod === "Stripe" ? "0 2px 12px 0 rgba(123,63,228,0.10)" : "none",
                    border: selectedPaymentMethod === "Stripe" ? "2px solid #7B3FE4" : "2px solid #ddd",
                    color: selectedPaymentMethod === "Stripe" ? "#7B3FE4" : "#777",
                    fontWeight: selectedPaymentMethod === "Stripe" ? 700 : 500,
                    fontSize: 14,
                    letterSpacing: "0.01em",
                    minWidth: 120,
                    padding: 12,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    background: "#fff",
                    transition: "all 0.2s"
                  }}
                >
                  Stripe
                  <img
                    src={stripeLogo}
                    style={{
                      height: 15,
                      borderRadius: 4,
                      marginLeft: 2,
                      opacity: selectedPaymentMethod === "Stripe" ? 1 : 0.7
                    }}
                  />
                </button>
                <button
                  onClick={() => setSelectedPaymentMethod("MetaMask")}
                  style={{
                    boxShadow: selectedPaymentMethod === "MetaMask" ? "0 2px 12px 0 rgba(123,63,228,0.10)" : "none",
                    border: selectedPaymentMethod === "MetaMask" ? "2px solid #7B3FE4" : "2px solid #777",
                    color: selectedPaymentMethod === "MetaMask" ? "#7B3FE4" : "#777",
                    fontWeight: selectedPaymentMethod === "MetaMask" ? 700 : 500,
                    fontSize: 14,
                    letterSpacing: "0.01em",
                    minWidth: 120,
                    margin: "0 16px 0 0",
                    padding: 12,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    background: "#fff",
                    transition: "all 0.2s"
                  }}
                >
                  MetaMask
                  <img
                    src={metaMaskLogo}
                    style={{
                      height: 15,
                      borderRadius: 4,
                      marginLeft: 2,
                      opacity: selectedPaymentMethod === "MetaMask" ? 1 : 0.7
                    }}
                  />
                </button>
              </div>
              <ol className="order-list grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
                <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                    Connect Wallet
                  </h3>
                  <p className="text-gray-600" style={{ marginBottom: 40 }}>
                    Connect your wallet using the button in the top right corner
                  </p>
                </li>
                <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                    Sign Up
                  </h3>
                  <p className="text-gray-600" style={{ marginBottom: 40 }}>
                    Complete the registration process with your email and personal information
                  </p>
                </li>
                <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                    Choose Payment Method
                  </h3>
                  <p className="text-gray-600" style={{ marginBottom: 40 }}>
                    Select your preferred payment method
                  </p>
                </li>
                {selectedPaymentMethod === "Stripe" && (
                  <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                      Proceed with Payment
                    </h3>
                    <p className="text-gray-600" style={{ marginBottom: 40 }}>
                      Proceed with payment through an external payment gateway
                    </p>
                  </li>
                )}
                {selectedPaymentMethod === "MetaMask" && (
                  <>
                    <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                        Set Allowance
                      </h3>
                      <p className="text-gray-600" style={{ marginBottom: 40 }}>
                        Set USDT allowance for NFT purchases in Account Information section
                      </p>
                    </li>
                    <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                        Check Allowance
                      </h3>
                      <p className="text-gray-600" style={{ marginBottom: 40 }}>
                        Verify your USDT allowance before making a purchase
                      </p>
                    </li>
                  </>
                )}
                <li className="flex flex-col items-center text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-purple-600 text-xl font-bold" style={{ marginBottom: 16 }}>
                    Complete Purchase
                  </h3>
                  <p className="text-gray-600" style={{ marginBottom: 40 }}>
                    Once payment is completed, your NFT purchase will be finalized
                  </p>
                </li>
              </ol>
            </div>
          </div>
        )}

        <div
          style={{
            width: "100%",
            display: "flex",
            height: "40px",
            marginBottom: 30
          }}
        >
          <div className={`tab-box ${selectedTab !== 0 ? "" : "selected"}`} onClick={() => setSearchParams({ tab: "Shop" })}>
            Shop
            {selectedTab === 0 && <div className="tab-indicator" />}
          </div>
          <div className={`tab-box ${selectedTab !== 1 ? "" : "selected"}`} onClick={() => setSearchParams({ tab: "My-NFTs" })}>
            My NFTs
            {selectedTab === 1 && <div className="tab-indicator" />}
          </div>
        </div>

        {selectedTab === 0 && (
          <>
            {/* 향상된 티어 탭 */}
            <div className="tier-tabs flex justify-center gap-4 mb-10 pt-4 pb-2">
              {tiers.map((tier) => (
                <button
                  key={tier}
                  className={`py-3 px-8 rounded-full font-semibold text-lg transition-all shadow-sm focus:outline-none ${selectedTier === tier ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-purple-100"}`}
                  onClick={() => setSelectedTier(tier)}
                  style={{
                    boxShadow: selectedTier === tier ? "0 2px 12px 0 rgba(123,63,228,0.10)" : "none",
                    border: selectedTier === tier ? "2px solid #7B3FE4" : "2px solid transparent",
                    fontWeight: selectedTier === tier ? 700 : 500,
                    letterSpacing: "0.01em",
                    minWidth: "120px",
                    margin: "0 16px 0 0"
                  }}
                >
                  {getTierName(tier)}
                </button>
              ))}
            </div>
            {/* 탭과 아래 텍스트 사이 간격 */}
            <div style={{ height: "32px" }}></div>
            {/* 선택된 티어만 표시 */}
            <div key={selectedTier}>
              <div
                className="text-center mb-10"
                style={{
                  display: "flex",
                  backgroundColor: "#F9FAFB",
                  padding: "32px 80px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  alignItems: "center"
                }}
              >
                <div style={{ flexGrow: 1 }}>
                  <h3
                    className="text-2xl font-bold text-gray-800 mb-4"
                    style={{
                      color: "#7B3FE4",
                      letterSpacing: "0.01em",
                      marginBottom: 10
                    }}
                  >
                    Key Benefits
                  </h3>
                  <p
                    className="text-gray-600"
                    style={{
                      maxWidth: "800px",
                      margin: "0 auto",
                      lineHeight: 1.8,
                      fontSize: "15px"
                    }}
                  >
                    {selectedTier === 1
                      ? "Each supporter can receive, 1 Nova, 2 Comet, and 3 Stardust + Exclusive Aetherium Avatar"
                      : selectedTier === 2
                      ? "Music & Streaming Royalty 1.5%, Voting & Event Pass, Behind-the-scenes access, Plan goods first and reservation for limited collaborations NFTs, Goods 35% discount, Fast NFT Notification Priority"
                      : selectedTier === 3
                      ? "Music Royalty 2%, Voting, Q&A, Avatar Utility, Community Rewards, Goods 20% Discount"
                      : "Music Royalty 1%, Voting, Digital 3.0% Badge, Airdrops, Goods 10% Discount, Fast News Notification Priority"}
                  </p>
                </div>
                <div style={{ height: 30, width: 2, margin: "auto 70px", backgroundColor: "#d9d9d9", borderRadius: 100 }} />
                <div style={{ flexShrink: 0 }}>
                  <p
                    className="text-2xl font-bold text-gray-800 mb-4"
                    style={{
                      color: "#777",
                      letterSpacing: "0.01em",
                      fontSize: "14px",
                      textAlign: "center"
                    }}
                  >
                    Price
                  </p>
                  <p
                    className="text-gray-600"
                    style={{
                      maxWidth: "800px",
                      lineHeight: 1.8,
                      fontSize: "24px",
                      fontWeight: 700,
                      marginBottom: 0,
                      color: "#7B3FE4"
                    }}
                  >
                    {nftTiers[selectedTier - 1].price} USDT
                  </p>
                </div>
              </div>
              <div className="nft-images-section mb-16">
                <div className="nft-image-grid">
                  {nftImages
                    .find((t) => t.tier === selectedTier)
                    ?.images.map((image, idx) => (
                      <div key={idx} className="nft-image-card shadow-lg hover:shadow-xl transition-all">
                        <div
                          style={{
                            width: "100%",
                            height: "200px",
                            position: "relative",
                            overflow: "visible",
                            backgroundColor: "#f3f4f6",
                            borderRadius: "0.75rem 0.75rem 0 0",
                            cursor: "pointer",
                            zIndex: 1
                          }}
                        >
                          <button
                            className="text-white text-sm font-medium rounded-lg backdrop-blur-sm flex items-center gap-1"
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              padding: "4px 8px",
                              zIndex: 3,
                              border: "1px solid rgba(123, 63, 228, 0.2)",
                              transition: "all 0.2s ease",
                              opacity: 0.7,
                              transform: "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = "1";
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = "0.7";
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                            onClick={() => {
                              setDetailItem(image);
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <polyline points="9 21 3 21 3 15"></polyline>
                              <line x1="21" y1="3" x2="14" y2="10"></line>
                              <line x1="3" y1="21" x2="10" y2="14"></line>
                            </svg>
                          </button>
                          <img
                            src={image.image}
                            alt={image.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              paddingTop: "12px",
                              paddingBottom: "12px",
                              transition: "all 0.3s ease",
                              zIndex: 2
                            }}
                          />
                        </div>
                        <div className="nft-image-info p-4">
                          <h4 className="font-bold text-gray-800">{image.name}</h4>
                          <p className="text-sm opacity-75 text-purple-600">ID: {image.image_id}</p>
                          <p
                            className="text-gray-600 my-2"
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: "100%"
                            }}
                          >
                            {image.description}
                          </p>
                          <button
                            onClick={() => {
                              document.body.style.overflow = "hidden";
                              setSelectedItem(image);
                              setIsOpenedPurchase(true);
                            }}
                            style={{
                              backgroundColor: "#a673ff",
                              color: "#fff",
                              height: 30,
                              padding: 0,
                              marginTop: 16
                            }}
                          >
                            Buy
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </>
        )}

        {selectedTab === 1 && (
          <>
            {/* 3. 보유 NFT 카드 */}
            {isConnected && userInfo ? (
              <div className="card-container">
                <div className="card-header">
                  <div>
                    <h2>Your NFT Collection</h2>
                    <p>Let's see what you've got!</p>
                  </div>
                  <button
                    onClick={getUserNFTs}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    disabled={loading}
                    style={{
                      backgroundColor: "#7b3fe4",
                      color: "white",
                      padding: "0.5rem 1rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      border: "none",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#7b3fe4";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#7b3fe4";
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38" />
                    </svg>
                    {loading ? "Loading..." : "Refresh Collection"}
                  </button>
                </div>
                <div className="card-content">
                  {/* NFT Collection Section */}
                  {loading ? (
                    <p className="text-gray-500 text-center">Loading...</p>
                  ) : userNFTs.length > 0 ? (
                    <div
                      className="nft-collection-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "2rem",
                        padding: "1rem"
                      }}
                    >
                      {userNFTs.map((nft, index) => (
                        <div
                          key={index}
                          className={`nft-collection-card`}
                          style={{
                            backgroundColor: "white",
                            borderRadius: "12px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                            overflow: "hidden",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            cursor: "pointer",
                            border: "1px solid #E5E7EB",
                            width: "100%",
                            maxWidth: "100%"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-4px)";
                            e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "none";
                            e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                          }}
                        >
                          <div
                            className="nft-collection-content"
                            style={{
                              width: "100%"
                            }}
                          >
                            <div
                              className="nft-collection-header"
                              style={{
                                padding: "1rem",
                                borderBottom: "1px solid #E5E7EB"
                              }}
                            >
                              <h3
                                className="nft-collection-title"
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: "600",
                                  color: "#111827",
                                  margin: "0"
                                }}
                              >
                                NFT #{nft.nft_info_id}
                              </h3>
                            </div>

                            <div
                              className="nft-collection-image"
                              style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "120%",
                                overflow: "hidden",
                                backgroundColor: "#f3f4f6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}
                            >
                              <img
                                src={nft.image_url}
                                alt={`NFT #${nft.nft_info_id}`}
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: "90%",
                                  height: "90%",
                                  objectFit: "contain",
                                  padding: "0.5rem"
                                }}
                              />
                            </div>
                            <div
                              className="nft-collection-info"
                              style={{
                                padding: "1rem"
                              }}
                            >
                              <div
                                className="nft-collection-item"
                                style={{
                                  marginBottom: "0.75rem"
                                }}
                              >
                                <span
                                  className="nft-collection-label"
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#6B7280",
                                    display: "block",
                                    marginBottom: "0.25rem"
                                  }}
                                >
                                  Tier
                                </span>
                                <span
                                  className="nft-collection-value"
                                  style={{
                                    fontSize: "1rem",
                                    color: "#111827",
                                    fontWeight: "500"
                                  }}
                                >
                                  {getTierName(nft.tier_id)}
                                </span>
                              </div>

                              <div
                                className="nft-collection-item"
                                style={{
                                  marginBottom: "0.75rem"
                                }}
                              >
                                <span
                                  className="nft-collection-label"
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#6B7280",
                                    display: "block",
                                    marginBottom: "0.25rem"
                                  }}
                                >
                                  Holder Address
                                </span>
                                <span
                                  className="nft-collection-value text-sm break-all"
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#111827",
                                    wordBreak: "break-all"
                                  }}
                                >
                                  {shortenAddress(nft.holder_address)}
                                </span>
                              </div>

                              <div
                                className="nft-collection-item"
                                style={{
                                  marginBottom: "0.75rem"
                                }}
                              >
                                <span
                                  className="nft-collection-label"
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#6B7280",
                                    display: "block",
                                    marginBottom: "0.25rem"
                                  }}
                                >
                                  NFT Info ID
                                </span>
                                <span
                                  className="nft-collection-value"
                                  style={{
                                    fontSize: "1rem",
                                    color: "#111827"
                                  }}
                                >
                                  {nft.nft_info_id}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        backgroundColor: "#F9FAFB",
                        borderRadius: "12px",
                        margin: "20px"
                      }}
                    >
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#7B3FE4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "16px" }}>
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                        <line x1="12" y1="22.08" x2="12" y2="12" />
                      </svg>
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#1F2937",
                          marginBottom: "8px"
                        }}
                      >
                        Looks like your collection is empty!
                      </h3>
                      <p
                        style={{
                          color: "#6B7280",
                          fontSize: "15px",
                          maxWidth: "400px",
                          margin: "0 auto",
                          lineHeight: "1.6"
                        }}
                      >
                        Start building your NFT collection by purchasing from our marketplace.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  margin: "20px"
                }}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#7B3FE4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "16px" }}>
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#1F2937",
                    marginBottom: "8px"
                  }}
                >
                  Looks like your collection is empty!
                </h3>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "15px",
                    maxWidth: "400px",
                    margin: "0 auto",
                    lineHeight: "1.6"
                  }}
                >
                  Start building your NFT collection by purchasing from our marketplace.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {detailItem !== undefined && (
        <div
          id="image-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 9998,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px"
          }}
          onClick={() => {
            setDetailItem();
          }}
        >
          <div
            className="modal"
            style={{
              width: "90%",
              maxWidth: "1400px",
              height: "90%",
              borderRadius: "16px",
              backgroundColor: "white",
              padding: "30px",
              display: "flex",
              overflow: "hidden",
              marginTop: 100
            }}
            onClick={(e) => e.stopPropagation()} // 내부 클릭 시 오버레이 닫히지 않도록
          >
            <img
              src={detailItem.image}
              alt="Preview"
              style={{
                height: "100%",
                objectFit: "contain",
                borderRadius: "16px",
                marginRight: "40px"
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                minWidth: "50%",
                flexGrow: 1,
                height: "100%"
              }}
            >
              {/* Header */}
              <div
                style={{
                  margin: "20px",
                  paddingBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "2px solid rgb(233, 233, 233)"
                }}
              >
                <h2 style={{ fontSize: 28, fontWeight: 800 }}>Information</h2>
                <img
                  src={closeImage}
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: "contain",
                    cursor: "pointer"
                  }}
                  onClick={() => setDetailItem()}
                />
              </div>
              <div
                className="information"
                style={{
                  margin: "0 20px",
                  marginBottom: 20,
                  overflow: "auto",
                  paddingRight: 16
                }}
              >
                <h2 style={{ margin: "30px 0" }}>
                  <span style={{ color: "#6935c4", marginRight: 8 }}>#{detailItem.image_id}</span>
                  {detailItem.name}
                </h2>

                {detailItem.group && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      group
                    </span>
                    {detailItem.group}
                  </p>
                )}
                {detailItem.position && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      position
                    </span>
                    {detailItem.position}
                  </p>
                )}
                {detailItem.nationality && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      nationality
                    </span>
                    {detailItem.nationality}
                  </p>
                )}
                {detailItem.birth && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      birth
                    </span>
                    {detailItem.birth}
                  </p>
                )}
                {detailItem.height && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      height
                    </span>
                    {detailItem.height}
                  </p>
                )}
                {detailItem.blood_type && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      blood type
                    </span>
                    {detailItem.blood_type}
                  </p>
                )}
                {detailItem.hobby && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      hobby
                    </span>
                    {detailItem.hobby}
                  </p>
                )}
                {detailItem.likes && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      likes
                    </span>
                    {detailItem.likes}
                  </p>
                )}
                {detailItem.traits && (
                  <p className="row">
                    <span className="label" style={{ width: 150, flexShrink: 0 }}>
                      traits
                    </span>
                    {detailItem.traits}
                  </p>
                )}
                <p
                  className="label"
                  style={{
                    width: 150,
                    marginTop: 40,
                    flexShrink: 0,
                    marginBottom: 10
                  }}
                >
                  description
                </p>
                {detailItem.description && (
                  <p
                    style={{
                      whiteSpace: "wrap",
                      marginBottom: 20,
                      padding: "20px 30px",
                      borderRadius: 6,
                      backgroundColor: "#f3f4f6",
                      display: "flex",
                      lineHeight: 2,
                      fontWeight: 300
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: detailItem.description_for_ui
                      }}
                    />
                  </p>
                )}
              </div>
              <div style={{ margin: "auto 20px 0" }}>
                <button
                  style={{ fontSize: 20 }}
                  onClick={() => {
                    if (selectedTab === 0) {
                      document.body.style.overflow = "hidden";
                      setSelectedItem(detailItem);
                      setIsOpenedPurchase(true);
                      setDetailItem();
                    } else {
                      // setNFTImage(selectedNFT, detailItem);
                    }
                  }}
                  // disabled={selectedTab === 1 && (imageLoading || !selectedNFT.is_image_editable)}
                >
                  {selectedTab === 0 ? "Buy" : "Set Image"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpenedPurchase && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.75)",
            zIndex: 99998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          onClick={() => setIsOpenedPurchase(false)}
        >
          <div
            className="modal"
            style={{
              width: "90%",
              maxWidth: "1400px",
              height: "90%",
              display: "flex",
              justifyContent: "space-around",
              padding: 30,
              backgroundColor: "#fff",
              borderRadius: 16,
              marginTop: 100
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                flexShrink: 1,
                height: "100%",
                borderRadius: 10,
                backgroundColor: "#fff",
                alignSelf: "center"
              }}
            >
              <img
                style={{
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: 16
                }}
                src={selectedItem.image}
              />
            </div>

            <div
              style={{
                flexGrow: 1,
                height: "100%",
                marginLeft: 30,
                display: "flex",
                flexDirection: "column"
              }}
            >
              {/* Header */}
              <div
                style={{
                  margin: "20px",
                  paddingBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "2px solid rgb(233, 233, 233)"
                }}
              >
                <h2 style={{ fontSize: 28, fontWeight: 800 }}>Get Your NFT</h2>
                <img
                  src={closeImage}
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: "contain",
                    cursor: "pointer"
                  }}
                  onClick={() => setIsOpenedPurchase(false)}
                />
              </div>
              <div style={{ height: "100%", overflow: "auto", padding: 20 }}>
                {/* 1. 계정 정보 카드 (지갑, 사용자 정보, Allowance) */}
                <div className="card-container">
                  <div className="card-header">
                    <div>
                      <h2>Account Information</h2>
                      <p>Manage your wallet and account details</p>
                    </div>
                    <ConnectButton.Custom>
                      {({ account, chain, openConnectModal, openChainModal, openAccountModal }) => {
                        const connected = account && chain;
                        return (
                          <button
                            onClick={connected ? openAccountModal : openConnectModal}
                            style={{
                              backgroundColor: "#7b3fe4",
                              color: "white",
                              padding: "12px 24px",
                              borderRadius: "8px",
                              border: "none",
                              fontSize: "16px",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#6b2fd4";
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#7b3fe4";
                              e.currentTarget.style.transform = "none";
                              e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                              <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                              <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
                            </svg>
                            {connected ? shortenAddress(account.address) : "Connect Wallet"}
                          </button>
                        );
                      }}
                    </ConnectButton.Custom>
                  </div>

                  <div className="card-content">
                    {/* 사용자 정보 섹션 */}
                    {isConnected ? (
                      <>
                        {loading ? (
                          <div className="text-gray-500 text-center py-4">Loading...</div>
                        ) : userInfo ? (
                          <>
                            <div className="card-section">
                              <div className="info-grid">
                                <div className="info-item">
                                  <label>Email</label>
                                  <p>{userInfo.email}</p>
                                </div>
                                <div className="info-item">
                                  <label>Name</label>
                                  <p>
                                    {userInfo.first_name} {userInfo.last_name}
                                  </p>
                                </div>
                                <div className="info-item">
                                  <label>Wallet Address</label>
                                  <p className="text-sm break-all">{shortenAddress(userInfo.address)}</p>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="card-section">
                            <div className="card-section-header">
                              <h3>Sign Up</h3>
                              <p>Please sign up to use our services.</p>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                  type="email"
                                  value={signupData.email}
                                  onChange={(e) =>
                                    setSignupData({
                                      ...signupData,
                                      email: e.target.value
                                    })
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="Enter your email"
                                  autoComplete="off"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                <input
                                  type="text"
                                  value={signupData.first_name}
                                  onChange={(e) =>
                                    setSignupData({
                                      ...signupData,
                                      first_name: e.target.value
                                    })
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="Enter your first name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                <input
                                  type="text"
                                  value={signupData.last_name}
                                  onChange={(e) =>
                                    setSignupData({
                                      ...signupData,
                                      last_name: e.target.value
                                    })
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="Enter your last name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Wallet Address</label>
                                <p className="text-sm break-all bg-gray-50 p-2 rounded">{shortenAddress(address)}</p>
                              </div>
                              <button onClick={signUp} className="sign-up-btn" disabled={loading || !signupData.email || !signupData.first_name || !signupData.last_name}>
                                {loading ? "Processing..." : "Sign Up"}
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray">Please connect your wallet to access the marketplace features</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. NFT 상점 카드 */}
                {isConnected && userInfo && (
                  <div className="card-container">
                    <div className="card-header">
                      <div>
                        <h2>NFT Shop</h2>
                        <p>Browse and purchase NFTs</p>
                      </div>
                    </div>
                    {tierList
                      .filter((tier) => tier.id === selectedTier)
                      .map((tier) => {
                        return (
                          <div className="card-content">
                            <div className="tier-grid">
                              <div key={tier.id} className="nft-card">
                                {/* NFT 이미지 */}
                                <div className="nft-card-image">
                                  {/* <div className={`w-32 h-32 relative ${tier.id === 1 ? "text-white" : tier.id === 2 ? "text-yellow-400" : tier.id === 3 ? "text-blue-400" : "text-pink-400"}`}> */}
                                  {/* <div className="absolute inset-0 shield-icon transform hover:scale-105 transition-transform" /> */}
                                  <img
                                    src={tier.id === 1 ? tier1 : tier.id === 2 ? tier2 : tier.id === 3 ? tier3 : tier4}
                                    style={{
                                      maxHeight: "100%",
                                      aspectRatio: 1
                                    }}
                                  />
                                  {/* </div> */}
                                </div>

                                {/* NFT 정보 */}
                                <div className="nft-card-content">
                                  <h3 className="nft-card-title">{tier.name}</h3>
                                  <p className="nft-card-price">${tier.price}</p>

                                  {/* 발행량 정보 */}
                                  <div className="nft-card-supply">
                                    <div className="nft-card-supply-item">
                                      <span className="nft-card-supply-label">Maximum Supply</span>
                                      <span className="nft-card-supply-value">{formatNumber(tier.maxSupply)}</span>
                                    </div>
                                    <div className="nft-card-supply-item">
                                      <span className="nft-card-supply-label">Current Supply</span>
                                      <span className="nft-card-supply-value">{formatNumber(tier.currentSupply)}</span>
                                    </div>
                                    {/* <div className="nft-card-supply-item">
                <span className="nft-card-supply-label">User Max Supply</span>
                <span className="nft-card-supply-value">{formatNumber(tier.userMaxSupply)}</span>
              </div> */}
                                  </div>

                                  {/* 혜택 목록 */}
                                  <ul className="nft-card-benefits">
                                    {tier.id === 1 &&
                                      ["Each supporter can receive", "1 Nova", "2 Comet", "3 Stardust", "Exclusive Aetherium Avatar"].map((benefit, idx) => (
                                        <li key={idx} className="nft-card-benefit">
                                          <span className="nft-card-benefit-check">✓</span> {benefit}
                                        </li>
                                      ))}
                                    {tier.id === 2 &&
                                      ["Music & Stream Royalty 1.5%", "Voting & Event pass", "Behind-the-scenes access", "Plan goods first and reservations for limited collaborations NFTs", "Goods 35% discount", "Fast NFT Notification Priority"].map((benefit, idx) => (
                                        <li key={idx} className="nft-card-benefit">
                                          <span className="nft-card-benefit-check">✓</span> {benefit}
                                        </li>
                                      ))}
                                    {tier.id === 3 &&
                                      ["Music Royalty 2%", "Voting", "Q&A", "Avatar utility", "Community rewards", "Goods 20% discount"].map((benefit, idx) => (
                                        <li key={idx} className="nft-card-benefit">
                                          <span className="nft-card-benefit-check">✓</span> {benefit}
                                        </li>
                                      ))}
                                    {tier.id === 4 &&
                                      ["Music Royalty 1%", "Voting", "Digital 3.0% badge", "Airdrops", "Goods 10% discount", "Fast News Notification Priority"].map((benefit, idx) => (
                                        <li key={idx} className="nft-card-benefit">
                                          <span className="nft-card-benefit-check">✓</span> {benefit}
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* PG 선택 */}
                            {!createdOrder && (
                              <div
                                className="card-section"
                                style={{
                                  borderBottom: "none",
                                  borderTop: "1px solid #e5e7eb",
                                  paddingTop: 30
                                }}
                              >
                                <div className="card-section-header">
                                  <h3>Choose Payment Method</h3>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                >
                                  <div
                                    style={{
                                      marginBottom: "16px",
                                      display: "flex",
                                      gap: 16,
                                      alignItems: "center"
                                    }}
                                  >
                                    <button
                                      onClick={() => setSelectedPaymentMethod("Stripe")}
                                      style={{
                                        boxShadow: selectedPaymentMethod === "Stripe" ? "0 2px 12px 0 rgba(123,63,228,0.10)" : "none",
                                        border: selectedPaymentMethod === "Stripe" ? "2px solid #7B3FE4" : "2px solid #ddd",
                                        color: selectedPaymentMethod === "Stripe" ? "#7B3FE4" : "#777",
                                        fontWeight: selectedPaymentMethod === "Stripe" ? 700 : 500,
                                        fontSize: 14,
                                        letterSpacing: "0.01em",
                                        minWidth: 120,
                                        padding: 12,
                                        borderRadius: 8,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 4,
                                        background: "#fff",
                                        transition: "all 0.2s"
                                      }}
                                    >
                                      Stripe
                                      <img
                                        src={stripeLogo}
                                        style={{
                                          height: 15,
                                          borderRadius: 4,
                                          marginLeft: 2,
                                          opacity: selectedPaymentMethod === "Stripe" ? 1 : 0.7
                                        }}
                                      />
                                    </button>
                                    <button
                                      onClick={() => setSelectedPaymentMethod("MetaMask")}
                                      style={{
                                        boxShadow: selectedPaymentMethod === "MetaMask" ? "0 2px 12px 0 rgba(123,63,228,0.10)" : "none",
                                        border: selectedPaymentMethod === "MetaMask" ? "2px solid #7B3FE4" : "2px solid #777",
                                        color: selectedPaymentMethod === "MetaMask" ? "#7B3FE4" : "#777",
                                        fontWeight: selectedPaymentMethod === "MetaMask" ? 700 : 500,
                                        fontSize: 14,
                                        letterSpacing: "0.01em",
                                        minWidth: 120,
                                        padding: 12,
                                        borderRadius: 8,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 4,
                                        background: "#fff",
                                        transition: "all 0.2s"
                                      }}
                                    >
                                      MetaMask
                                      <img
                                        src={metaMaskLogo}
                                        style={{
                                          height: 15,
                                          borderRadius: 4,
                                          marginLeft: 2,
                                          opacity: selectedPaymentMethod === "MetaMask" ? 1 : 0.7
                                        }}
                                      />
                                    </button>
                                    {selectedPaymentMethod === "MetaMask" && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          padding: "8px 16px",
                                          backgroundColor: "#FEF3C7",
                                          borderRadius: 8,
                                          border: "1px solid #F59E0B",
                                          color: "#92400E",
                                          fontSize: 13,
                                          fontWeight: 500,
                                          animation: "fadeIn 0.3s ease-in-out"
                                        }}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="12" cy="12" r="10" />
                                          <line x1="12" y1="8" x2="12" y2="12" />
                                          <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        Only Ethereum network is supported for MetaMask payments.
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Allowance 섹션 */}
                                {selectedPaymentMethod === "MetaMask" && (
                                  <div className="info-item" style={{ marginTop: 10 }}>
                                    <label>USDT Allowance</label>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between"
                                      }}
                                    >
                                      <p>{currentAllowance ? `${(BigInt(currentAllowance) / BigInt(10n ** 6n)).toString()} USDT` : "Check Required"}</p>
                                      <button onClick={getAllowance} className="check-allowance-btn" disabled={loading} style={{ padding: "8px 14px" }}>
                                        {loading ? "Loading..." : "Check Allowance"}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 버튼 */}
                            {!isConnected ? (
                              <button className="nft-card-button nft-card-button-disabled" disabled>
                                Connect Wallet Required
                              </button>
                            ) : !userInfo ? (
                              <button className="nft-card-button nft-card-button-disabled" disabled>
                                Sign Up Required
                              </button>
                            ) : createdOrder ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 16
                                }}
                              >
                                <button
                                  className="nft-card-button nft-card-button-normal"
                                  onClick={() => {
                                    getUserNFTs();
                                    setSearchParams({ tab: "My-NFTs" });
                                    setIsOpenedPurchase(false);
                                  }}
                                >
                                  Go to My NFTs
                                </button>
                                <button className={"retry-payment"} onClick={() => setCreatedOrder(false)} style={{ fontSize: 14, fontWeight: 600 }}>
                                  Retry Payment
                                </button>
                              </div>
                            ) : (
                              <>
                                {selectedPaymentMethod === "Stripe" ? (
                                  <button className="nft-card-button nft-card-button-primary" onClick={() => createOrder()} disabled={loading}>
                                    {loading ? "Processing..." : "Buy"}
                                  </button>
                                ) : currentAllowance && !Numbers.lt((BigInt(currentAllowance) / BigInt(10n ** 6n)).toString(), tier.price) ? (
                                  <button className="nft-card-button nft-card-button-primary" onClick={() => createOrderByWeb3()} disabled={loading}>
                                    {loading ? "Processing..." : "Buy"}
                                  </button>
                                ) : (
                                  <>
                                    <p
                                      style={{
                                        color: "#4a4a4a",
                                        fontSize: 13,
                                        marginLeft: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center"
                                      }}
                                    >
                                      Check your USDT allowance before payment
                                    </p>
                                    <button className="nft-card-button nft-card-button-primary" onClick={() => setAllowance(tier.price)} disabled={loading}>
                                      {loading ? "Processing..." : "Set Allowance"}
                                    </button>
                                  </>
                                )}
                              </>
                            )}

                            {/* Prod 임시 버튼 */}
                            {/* <div
                              style={{ position: "relative" }}
                              onMouseEnter={(e) => {
                                const tooltip = e.currentTarget.querySelector(".tooltip-buy");
                                if (tooltip) {
                                  tooltip.style.opacity = "1";
                                  tooltip.style.transform = "translateY(0)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                const tooltip = e.currentTarget.querySelector(".tooltip-buy");
                                if (tooltip) {
                                  tooltip.style.opacity = "0";
                                  tooltip.style.transform = "translateY(10px)";
                                }
                              }}
                            >
                              <button className="nft-card-button nft-card-button-disabled" disabled>
                                Buy
                                <div
                                  className="tooltip-buy"
                                  style={{
                                    position: "absolute",
                                    bottom: "100%",
                                    left: "calc(50% - 50px)",
                                    transform: "translateX(-50%) translateY(10px)",
                                    backgroundColor: "#7B3FE4",
                                    color: "white",
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                    opacity: 0,
                                    transition: "all 0.2s ease",
                                    pointerEvents: "none",
                                    marginBottom: "8px",
                                    boxShadow: "0 4px 12px rgba(123, 63, 228, 0.2)"
                                  }}
                                >
                                  Coming soon
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-4px",
                                      left: "50%",
                                      transform: "translateX(-50%) rotate(45deg)",
                                      width: "8px",
                                      height: "8px",
                                      backgroundColor: "#7B3FE4"
                                    }}
                                  />
                                </div>
                              </button>
                            </div> */}

                            {/* Web2 테스트 임시 버튼 */}
                            {/* {createdOrder ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                                <button
                                  className="nft-card-button nft-card-button-normal"
                                  onClick={() => {
                                    getUserNFTs();
                                    setSearchParams({ tab: "My-NFTs" });
                                    setIsOpenedPurchase(false);
                                  }}
                                >
                                  Go to My NFTs
                                </button>
                                <button className={"retry-payment"} onClick={() => setCreatedOrder(false)} style={{ fontSize: 14, fontWeight: 600 }}>
                                  Retry Payment
                                </button>
                              </div>
                            ) : (
                              <>
                                <button className="nft-card-button nft-card-button-primary" onClick={() => createOrder()} disabled={loading}>
                                  {loading ? "Processing..." : "Buy for Stripe"}
                                </button>
                              </>
                            )} */}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default UserView;
